import { FC, useEffect, useRef, useState } from "react";
import {  useParams } from "react-router-dom";
import Table from "../../../../components/common/Table/Table";
import CardTable from "../../../../components/common/CardTable";
import characterCountInit from "form-pattern/src/scripts/form-character-count";
import InfoIcon from "../../../../../public/assets/images/info-circle-small-solid.svg";
import uploadOutline from "../../../../../public/assets/images/uploadOutline.svg";
import downloadOutline from "../../../../../public/assets/images/downloadOutline.svg";
import { useDispatch } from "react-redux";
import {
  downloadDocument,
  fetchApplicationDetails,
  getDocumentsList,
  uploadDocument,
  // downloadDocument,
} from "../../../../store/slices/Tuition/tuitionViewMyStatusSlice";
import { useAppSelector } from "../../../../store/hooks";
import Loader from "../../../../components/common/Loader";
import { uploadDocumentFields } from "../../../../config/viewDocumentsFields";
import { validateForm } from "../../../../utils/validationEngine";
import Notifications from "../../../../components/common/Notifications";
import UploadErrorBanner from "../../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import { trackButtonClick, trackDocumentUpload, trackFormError } from '../../../../services/analytics';

type TableColumn = {
  key: string;
  label: string;
};

const columns: TableColumn[] = [
  { key: "documentName", label: "Document Name" },
  { key: "contentDescription", label: "Document content description" },
  {key:"createdTime",label:"Date added"},
  {key:"lastUpdatedBy",label:"Uploaded by"},
  { key: "employeeUploadFlag", label: "Employee upload flag" },
  { key: "action", label: "Action" },
];

type TableRow = {
  documentName: string;
  contentDescription: string;
  createdTime: string;
  lastUpdatedBy: string;
  createdBy: string;
  employeeUploadFlag: string;
  id: string;
};

const DownloadButton = ({ onClick }: { onClick?: () => void }) => (
  <button className="button -primary -small" onClick={onClick}>
    <img
      src={downloadOutline}
      alt="download icon"
      className="inline-block align-middle mr-2 w-6! h-6!"
      aria-hidden="true"
    />
    <span>Download</span>
  </button>
);

const TuitionMyDocumentsTab: FC = () => {
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [contentDescription, setContentDescription] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const [fileFormatError, setFileFormatError] = useState<string>("");
  const [fileSizeError, setFileSizeError] = useState<string>("");
  const [noFileSelectedError, setNoFileSelectedError] = useState<string>("");
  const [downloadError, setDownloadError] = useState<string>("");
  const { id } = useParams<{ id: string }>();
  const [documents, setDocuments] = useState<TableRow[]>([]);
  const { loading } = useAppSelector((state) => state.viewDocument);
    const successToastRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

    // Scroll to success toast when it appears
  useEffect(() => {
    if (showSuccessToast && successToastRef.current) {
      successToastRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [showSuccessToast]);
  
  const fetchDocuments = async () => {
    if (!id) {
      console.error("No application id provided.");
      setDocuments([]);
      return;
    }
    try {
      const action = await dispatch<any>(
        getDocumentsList({ applicationId: id })
      );
      if (getDocumentsList.fulfilled.match(action)) {
        const payload = action.payload;

        if (payload?.items) {
          setDocuments(payload.items);
        } else {
          setDocuments([]);
        }
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setDocuments([]);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        characterCountInit();
        console.log("Character count initialized");
      } catch (error) {
        console.error("Character count init error:", error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [id]);

  // Auto-dismiss download error after 10 seconds
  useEffect(() => {
    if (!downloadError) return;
    
    const timer = setTimeout(() => {
      setDownloadError("");
    }, 10000);

    return () => clearTimeout(timer);
  }, [downloadError]);

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File checks are independent of the description, so the error shows immediately.
    const formatError = validateFileFormat(file);
    const sizeError = formatError ? null : validateFileSize(file);

    setNoFileSelectedError("");
    setFileFormatError(formatError ?? "");
    setFileSizeError(sizeError ?? "");
    // Keep the file so its name stays visible; handleUpload re-validates it before sending.
    setSelectedFile(file);
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setContentDescription("");
    setValidationError("");
    setFileFormatError("");
    setFileSizeError("");
    setNoFileSelectedError("");
    const textarea = document.getElementById(
      "text-area-6"
    ) as HTMLTextAreaElement;
    if (textarea) {
      textarea.value = "";
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContentDescription(e.target.value);
    if (validationError && e.target.value.trim()) {
      setValidationError("");
    }
    // Clear no file selected error when user starts typing
    if (noFileSelectedError && e.target.value.trim()) {
      setNoFileSelectedError("");
    }
  };

  // Validation helper functions
  const validateFileFormat = (file: File): string | null => {
    const fileFormatData = {
      documentFile: file.name,
      documentFileSize: 0,
      contentDescription: "",
    };

    const fileFormatValidation = validateForm(
      uploadDocumentFields.filter((field) => field.name === "documentFile"),
      fileFormatData
    );

    if (
      !fileFormatValidation.isValid &&
      fileFormatValidation.errors["documentFile"]
    ) {
      return fileFormatValidation.errors["documentFile"];
    }
    return null;
  };

  const validateFileSize = (file: File): string | null => {
    const fileSizeData = {
      documentFile: file.name,
      documentFileSize: file.size,
      contentDescription: "",
    };

    const fileSizeValidation = validateForm(
      uploadDocumentFields.filter((field) => field.name === "documentFileSize"),
      fileSizeData
    );

    if (
      !fileSizeValidation.isValid &&
      fileSizeValidation.errors["documentFileSize"]
    ) {
      return fileSizeValidation.errors["documentFileSize"];
    }
    return null;
  };

  const validateContentDescription = (
    file: File,
    description: string
  ): string | null => {
    const fullFormData = {
      documentFile: file.name,
      documentFileSize: file.size,
      contentDescription: description,
    };

    const contentValidation = validateForm(uploadDocumentFields, fullFormData);

    if (!contentValidation.isValid) {
      if (contentValidation.requiredIfErrors?.["contentDescription"]) {
        return contentValidation.requiredIfErrors["contentDescription"];
      }
      if (contentValidation.errors["contentDescription"]) {
        return contentValidation.errors["contentDescription"];
      }
    }
    return null;
  };

  const scrollToTextarea = () => {
    const textarea = document.getElementById("text-area-6");
    if (textarea) {
      textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      textarea.focus();
    }
  };

  const performUpload = async (
    file: File,
    applicationId: string,
    description: string
  ) => {
    try {
      const action = await dispatch<any>(
        uploadDocument({
          applicationId,
          file,
          description,
        })
      );

      if (uploadDocument.fulfilled.match(action)) {
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 25000);

        // Clear fields
        setSelectedFile(null);
        setContentDescription("");
        const textArea = document.getElementById(
          "text-area-6"
        ) as HTMLTextAreaElement;
        if (textArea) textArea.value = "";
        
        // Reset file input to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Fetch updated documents and application details
        await fetchDocuments();
        await dispatch<any>(
          fetchApplicationDetails(applicationId)
        );
      } else {
        console.error("Failed to upload document:", action.payload);
      }
    } catch (error) {
      console.error("Error during document upload:", error);
    }
  };

  const handleUpload = async () => {
    console.log('📊 Analytics: Upload document clicked')
    trackButtonClick('Upload', 'tuition-my-documents')
    // Early return if no file selected
    if (!selectedFile) {
      setNoFileSelectedError("Please choose a document to upload.");
      setFileFormatError("");
      setFileSizeError("");
      setValidationError("");
      console.log('📊 Analytics: Upload error - No file selected')
      trackFormError('tuition', ['fileSelection'])
      return;
    }

    // Clear no file selected error
    setNoFileSelectedError("");

    // Validate file format (Priority 1); the file stays selected so its name remains visible
    const formatError = validateFileFormat(selectedFile);
    if (formatError) {
      setFileFormatError(formatError);
      setFileSizeError("");
      console.log('📊 Analytics: Upload error - File format validation failed')
      trackFormError('tuition', ['fileFormat'])
      return;
    }
    setFileFormatError("");

    // Validate file size (Priority 2)
    const sizeError = validateFileSize(selectedFile);
    if (sizeError) {
      setFileSizeError(sizeError);
      console.log('📊 Analytics: Upload error - File size validation failed')
      trackFormError('tuition', ['fileSize'])
      return;
    }
    setFileSizeError("");

    // Validate content description (Priority 3)
    const contentError = validateContentDescription(
      selectedFile,
      contentDescription
    );
    if (contentError) {
      setValidationError(contentError);
      scrollToTextarea();
      console.log('📊 Analytics: Upload error - Content description validation failed')
      trackFormError('tuition', ['contentDescription'])
      return;
    }

    // Clear all validation errors
    setValidationError("");
    setFileFormatError("");
    setFileSizeError("");

    // Perform upload
    if (id) {
      const description =
        (document.getElementById("text-area-6") as HTMLTextAreaElement)?.value ||
        "";
      console.log('📊 Analytics: Document upload initiated')
      trackDocumentUpload('tuition', selectedFile.name, true)
      await performUpload(selectedFile, id, description);
    } else {
      alert("Please select a file and ensure application ID is available.");
    }
  };

  const handleDownload = async (documentId: string,fileName?: string) => {
    console.log(`📊 Analytics: Download clicked - Document ${documentId}`)
    trackButtonClick('Download', 'tuition-my-documents', { documentId })
    if (!id) {
      console.error("No application id provided.");
      return;
    }

    const action = await dispatch<any>(
      downloadDocument({ applicationId: id, documentId,fileName })
    );

    if (downloadDocument.fulfilled.match(action)) {
      console.log("Document downloaded successfully.");
      trackDocumentUpload('tuition', fileName || documentId, true)
    } else {
      console.error("Failed to download document:", action.payload);
      setDownloadError(action.payload as string || "Failed to download document");
    }
  };

  return (
    <div className="margin-top-4u">
      {loading && <Loader />}
      {downloadError && (
        <div className="margin-top-4u">
          <Notifications messageInfo={downloadError} status={false} />
        </div>
      )}

      <div className="margin-top-4u lg:mx-[40px]!">
        <p className="p3 -book !mb-[24px]">
          On this page, you can{" "}
          <strong>manage documents associated with your application.</strong>{" "}
          You have two main actions:
        </p>
        <ul className="list-disc !ml-[20px]">
          <li>
            <p className="p3 -book !mb-[8px]">
              <strong>Download Document:</strong> Review the list of available
              documents and select the one you need. You can now download{" "}
              <strong>one file at a time</strong> in its original format.
            </p>
          </li>
          <li>
            <p className="p3 -book !mb-[24px]">
              <strong>Upload Documents:</strong> If you need to provide
              additional documentation, you can upload files using the section
              below. Make sure to follow the upload guidelines for file type
              and size.
            </p>
          </li>
        </ul>
        {/* Mobile: CardTable wrapper for heading, description, and table */}

        <CardTable
          className="mb-8 px-4 mx-5 sm:mx-0 sm:px-0"
          aria-label="My documents table"
        >
          <h3 className="h3">My documents</h3>
          <div className="lg:mt-[20px]!">
            {documents.length > 0 ? (
              <>
                <p className="p3 -book mb-4!">
                  Documents associated with this application
                </p>
                <Table
                  columns={columns}
                  rows={documents.map((row) => ({
                    ...row,
                    employeeUploadFlag: row.employeeUploadFlag === 'Y' ? 'Yes': 'No',
                    lastUpdatedBy: row.lastUpdatedBy ?? row.createdBy,
                    action: (
                      <DownloadButton onClick={() => handleDownload(row.id,row.documentName)} />
                    ),
                  }))}
                />
              </>
            ) : (
              !loading && (
                <p className="p3 -book text-center mt-[10px]! text-red-600">
                  No documents found for this application.
                </p>
              )
            )}
          </div>
        </CardTable>
      </div>

      <hr className="-variation-1" />
      {showSuccessToast && (
         <div ref={successToastRef}>
        <Notifications
          messageInfo="Document has been uploaded successfully."
          status={true}
        />
      </div>
      )}
      <h2 className="h2">Upload documents</h2>

      {/* Upload Section */}
      <div>
        <p className="p3 -book">
          upload documents for application
        </p>

        <h3 className="h3 margin-top-2u">How to upload:</h3>
        <p className="p3 -book">
          To upload a document, please follow the steps below.
        </p>

        {/* Instructions */}
        <ol className="mb-4">
          <li className="p3 -boook mt-6!"> Select the application.</li>
          <li className="p3">
            1. Click <b>Choose File</b> to select the document.
          </li>
          <li className="p3">2. Enter a content description.</li>
          <li className="p3">
            3. Click <b>Upload</b> to attach the document.
          </li>
        </ol>

        <ul className="list-disc pl-6 !mt-0 marker:text-[#D1480C] !ml-[14px]">
          <li className="!mt-6 text-[#D1480C] p3 -bbok !mb p3 -book">
            Required fields
          </li>
          <li className="p3 -bbok">
            Upload New Document (Upload only pdf and image files).
          </li>
          <li className="p3 -book">
            You must provide a description of the contents when uploading a
            document.
          </li>
          <li className="p3 -book">
            Clear button to clear the input fields before uploading.
          </li>
        </ul>

        {noFileSelectedError && (
          <UploadErrorBanner
            header="No file selected"
            message={noFileSelectedError}
            className="w-[70%]"
          />
        )}

        {fileFormatError && (
          <UploadErrorBanner
            header="Incorrect file type"
            message="The selected file type is not supported. Please upload a PDF or image file."
            className="w-[70%]"
          />
        )}

        {fileSizeError && (
          <UploadErrorBanner
            header="File size exceeds limit"
            message="The selected file is too large. Please upload a file that is 10 MB or smaller."
            className="w-[70%]"
          />
        )}

        <div className="inline-flex items-center gap-2 border border-gray-300 bg-[#F7F7F7] text-[#878F9B] !p-4 !mt-[20px]">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".pdf,.docx,.docx,.xls,.xlsx,image/*"
            data-testid="file-input"
          />

          <button className="button" onClick={handleChooseFile}>
            <img
              src={uploadOutline}
              alt="upload icon"
              className="!w-[1.6rem] !h-[1.25rem] inline-block align-middle"
              aria-hidden="true"
            />
            <span>Choose File</span>
          </button>

          {(() => {
            if (fileFormatError) {
              return (
                <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {fileFormatError}
                </p>
              );
            }
            if (fileSizeError) {
              return (
                <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {fileSizeError}
                </p>
              );
            }
            return <p className="p3 -dolphin !mt-0">Max file size 10 MB</p>;
          })()}
        </div>

        {selectedFile && (
          <div className="mt-2">
            <span>Selected file: {selectedFile.name}</span>
          </div>
        )}
      </div>

      {validationError && (
        <UploadErrorBanner
          header="Upload document comment is missing"
          message="Please add a comment for the uploaded document before continuing."
          className="w-[70%]"
        />
      )}

      <div className="form-group margin-top-2u">
        <label
          id="textarea-label2"
          className="p4 -medium after:content-['*'] after:text-[#D1480C]"
          htmlFor="text-area-6"
          aria-required={true}
        >
          Describe the contents
        </label>
        <div className="textarea-wrapper">
          <textarea
            autoComplete="on"
            id="text-area-6"
            aria-labelledby="textarea-label2 textarea_count2"
            aria-describedby="textarea-error-description-list-2"
            className={`text-area_word-count !text-[#0D1C3D] text-area_global -book ${
              validationError && !contentDescription.trim() ? "field-error" : ""
            }`}
            data-show-lines="5"
            maxLength={1000}
            onChange={handleContentChange}
            value={contentDescription}
          ></textarea>
          {!(validationError && !contentDescription.trim()) && (
            <div className="character-count">
              <span
                id="textarea_count2"
                aria-live="polite"
                aria-atomic="true"
                className="count-area text-input-helper"
                data-description=" of 1000 characters left"
                data-full-text="No more characters available, 1000 of 1000"
              >
                {contentDescription.length===0?`1000 of 1000 characters left`:`${1000 - contentDescription.length} of 1000 characters left`}
              </span>
            </div>
          )}
        </div>
        {validationError && !contentDescription.trim() && (
          <div className="error-description mt-2">
            <ul id="textarea-error-description-list-2">
              <li>
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="Error icon"
                />
                <span className="screenreader-only">Error</span>
                {validationError}
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-4 margin-top-2u sm:flex-row">
        <button className="button -light" onClick={handleCancel}>
          Clear information
        </button>

        <button className="button -primary -light" onClick={handleUpload}>
          Upload document
        </button>
      </div>

      <div className="relative !mt-6 !mb-4 flex gap-2">
        <img src={InfoIcon} alt="info icon" />
        <p className="p3 -book">
          If you select clear button you may need to re-enter your description
          and re-upload documentation
        </p>
      </div>
    </div>
  );
};

export default TuitionMyDocumentsTab;
